import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { buildLeavePrefill } from "../utils/leavePrefill";
import styles from "./EventCreateModal.module.css";
import api from "../api"; // ✅ axios 인스턴스 (JWT 토큰 포함되어야 함)
import WorkCategoryManageModal from "./WorkCategoryManageModal";
import { isActiveStaff } from "../utils/contractEnded";
import {
  TITLE_INPUT_TYPES, WORKDAY_SPLIT_TYPES, REQUEST_ONLY_NOTICE, currentUser, isCalendarAdmin,
  selectableEventTypesFor,
  NEEDS_HOLIDAY_LOOKUP_TYPES, PERFORMANCE_INPUT_TYPES,
} from "../constants/eventTypes";

// ✅ 근무 요일 (0=일 ~ 6=토)
const WEEKDAYS = [
  { dow: 1, label: "월" },
  { dow: 2, label: "화" },
  { dow: 3, label: "수" },
  { dow: 4, label: "목" },
  { dow: 5, label: "금" },
  { dow: 6, label: "토" },
  { dow: 0, label: "일" },
];
// 기본: 평일만 등록 (토/일 제외)
const DEFAULT_WORKDAYS = { 0: false, 1: true, 2: true, 3: true, 4: true, 5: true, 6: false };

// initialRange — 캘린더에서 드래그로 고른 범위 {startDate, endDate, startTime, endTime}(시각은 종일이면 "")
export default function EventCreateModal({ isOpen, onClose, onSubmit, initialRange = null }) {
  const navigate = useNavigate();
  // 한국 시간 기준으로 현재 날짜 가져오기 (YYYY-MM-DD)
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [step, setStep] = useState(1); // ✅ 단계 관리 (1: 담당자, 2: 일정유형, 3: 시간/상세)

  const [formData, setFormData] = useState({
    title: "",
    event_type: "업무일정",
    startDate: getLocalDateString(),
    endDate: getLocalDateString(),
    startTime: "",
    endTime: "",
    description: "",
    assignee: "",
    break_hours: 0,
    // ✅ 업무일정 전용 필드
    work_category: "",   // WorkCategory id
    location: "",        // 장소
    difficulty: "1",      // 1~10
    travel_hours: 0,     // 이동시간
  });

  const [users, setUsers] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [selectedUserBalance, setSelectedUserBalance] = useState(null);

  // ✅ 업무종류 목록 + 장소 자동완성 후보
  const [workCategories, setWorkCategories] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showCategoryManage, setShowCategoryManage] = useState(false);  

  // ✅ 근무일/공휴일 근무 상태
  const [workDays, setWorkDays] = useState(DEFAULT_WORKDAYS);
  const [holidaysInRange, setHolidaysInRange] = useState([]);
  const [workedHolidays, setWorkedHolidays] = useState({}); // { "2026-06-03": true }


  // ✅ 사용자 목록 + 휴가 잔여시간 + 업무종류 불러오기
  useEffect(() => {
    if (isOpen) {
      api
        .get("/users/staff-contacts/")
        .then((res) => setUsers(res.data))
        .catch((err) => console.error("❌ 유저 목록 불러오기 실패:", err));

      api
        .get("/calendar/leave-balance/")
        .then((res) => setLeaveBalances(res.data))
        .catch((err) => console.error("❌ 휴가 잔여시간 불러오기 실패:", err));

      // ✅ 업무종류 불러오기
      api
        .get("/calendar/work-categories/")
        .then((res) => setWorkCategories(res.data || []))
        .catch((err) => console.error("❌ 업무종류 불러오기 실패:", err));
    }
  }, [isOpen]);

  // ✅ 장소 자동완성: location 입력 시 후보 조회
  useEffect(() => {
    if (!isOpen) return;
    if (formData.event_type !== "업무일정") return;

    const handler = setTimeout(() => {
      api
        .get("/calendar/locations/suggest/", {
          params: { q: formData.location || "" },
        })
        .then((res) => setLocationSuggestions(res.data || []))
        .catch(() => setLocationSuggestions([]));
    }, 200); // 입력 디바운스 200ms

    return () => clearTimeout(handler);
  }, [formData.location, formData.event_type, isOpen]);

  // 선택된 유저 잔여시간 매칭
  useEffect(() => {
    if (formData.assignee && users.length > 0 && leaveBalances.length > 0) {
      const selectedUser = users.find(u => u.id === parseInt(formData.assignee));
      if (selectedUser) {
        const userBalance = leaveBalances.find(
          balance => balance.assignee_display === selectedUser.name
        );
        setSelectedUserBalance(userBalance);
      }
    } else {
      setSelectedUserBalance(null);
    }
  }, [formData.assignee, users, leaveBalances]);

  // ✅ 모달 열릴 때 step + 근무일 초기화
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setWorkDays(DEFAULT_WORKDAYS);
      setWorkedHolidays({});
    }
  }, [isOpen]);

  // ✅ 기간 내 공휴일 조회 (업무일정/행사 전용)
  useEffect(() => {
    if (!isOpen) return;
    // ✅ 휴가 4종도 공휴일이 필요하다 — 차감 대상 근무일에서 공휴일을 빼야 하므로
    // (예전엔 업무일정/행사만 조회해서 휴가 신청 시간이 공휴일까지 세고 있었다).
    if (!NEEDS_HOLIDAY_LOOKUP_TYPES.includes(formData.event_type)) {
      setHolidaysInRange([]);
      return;
    }
    if (!formData.startDate || !formData.endDate) return;

    api
      .get("/calendar/holidays/", {
        params: { start: formData.startDate, end: formData.endDate },
      })
      .then((res) => setHolidaysInRange(res.data || []))
      .catch(() => setHolidaysInRange([]));
  }, [isOpen, formData.startDate, formData.endDate, formData.event_type]);



  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  function generateTimeOptions() {
    const options = [];
    for (let h = 0; h < 24; h++) {
      let timeStep = h >= 9 && h < 18 ? 5 : 10;
      for (let m = 0; m < 60; m += timeStep) {
        options.push(
          `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
        );
      }
    }
    return options;
  }

  useEffect(() => {
    if (!isOpen) return;
    const now = new Date();
    let hour = now.getHours();
    let minute = now.getMinutes();
    let timeStep = hour >= 9 && hour < 18 ? 5 : 10;
    minute = Math.ceil(minute / timeStep) * timeStep;
    if (minute === 60) {
      minute = 0;
      hour += 1;
    }
    const start = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    const end = `${String(hour + 1).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    setFormData((prev) => ({ ...prev, startTime: start, endTime: end }));
  }, [isOpen]);

  // ✅ 드래그로 고른 범위로 채운다(2026-10-19) — 위 "지금 시각" 기본값보다 뒤에 돌아 덮어쓴다
  useEffect(() => {
    if (!isOpen || !initialRange) return;
    setFormData((prev) => ({
      ...prev,
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
      ...(initialRange.startTime ? { startTime: initialRange.startTime, endTime: initialRange.endTime } : {}),
    }));
  }, [isOpen, initialRange]);

  // ✅ 일반 직원의 연차·반차 → 휴가신청서 작성 화면으로 이동 + 프리필만(일정은 만들지 않는다)
  const goLeaveRequest = () => {
    const range = initialRange || { startDate: formData.startDate, endDate: formData.endDate };
    onClose();
    navigate(`/approval/leave/new?${buildLeavePrefill(range)}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "break_hours" || name === "travel_hours") {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: String(value) }));
    }
  };

  const normalizeTime = (value, fallback) => {
    if (!value) return fallback;
    return value.length === 5 ? value + ":00" : value;
  };

  // ✅ [2026-08-13 버그 수정] 휴가 신청 시간 = 하루 근무시간 × 근무일 수.
  // 예전에는 "종료일시 − 시작일시 − 휴게 1회"라 여러 날 휴가에서 밤·새벽까지
  // 휴가로 셌다(8/12~8/14 09:00~18:00 휴게 1시간 → 56시간, 기대값 24시간).
  // 백엔드 calendar_app/leave_utils.py와 같은 산식이라 화면 표시·사전 알림과
  // 실제 차감액이 어긋나지 않는다.
  const buildLeaveDates = () => {
    if (!formData.startDate) return [];
    const [sy, sm, sd] = formData.startDate.split("-").map(Number);
    const [ey, em, ed] = (formData.endDate || formData.startDate).split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const holidayDates = new Set(holidaysInRange.map((h) => h.date));
    const result = [];
    while (cur <= end) {
      const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      const dow = cur.getDay(); // 0=일 ~ 6=토
      // 휴가는 근무 요일 체크박스(업무일정 전용 UI)를 쓰지 않는다 — 주말·공휴일 고정 제외
      if (dow !== 0 && dow !== 6 && !holidayDates.has(iso)) result.push(iso);
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  };

  const calculateRequestedHours = () => {
    if (!formData.startDate || !formData.startTime || !formData.endTime) {
      return 0;
    }
    const perDay = calcDayHours();
    if (perDay <= 0) return -1;
    const days = buildLeaveDates().length;
    if (days === 0) return 0;   // 기간이 전부 주말·공휴일 — 제출 시 차단
    return parseFloat((perDay * days).toFixed(2));
  };

  // ✅ 하루 단위 근무시간 계산 (업무일정/행사용)
  const calcDayHours = () => {
    if (!formData.startTime || !formData.endTime) return 0;
    const [sh, sm] = formData.startTime.split(":").map(Number);
    const [eh, em] = formData.endTime.split(":").map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff / 60 - (formData.break_hours || 0);
  };

  // ✅ 등록 대상 근무일 계산 (주말/공휴일 자동 제외 + 체크로 override)
  const buildWorkingDates = () => {
    if (!formData.startDate) return [];
    const [sy, sm, sd] = formData.startDate.split("-").map(Number);
    const [ey, em, ed] = (formData.endDate || formData.startDate).split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const holidayDates = new Set(holidaysInRange.map((h) => h.date));
    const result = [];

    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      const iso = `${y}-${m}-${d}`;
      const dow = cur.getDay(); // 0=일 ~ 6=토

      if (holidayDates.has(iso)) {
        // 공휴일: 체크된 날만 등록
        if (workedHolidays[iso]) result.push(iso);
      } else {
        // 일반일: 체크된 요일만 등록
        if (workDays[dow]) result.push(iso);
      }
      cur.setDate(cur.getDate() + 1);
    }
    return result;
  };

  const checkLeaveBalance = () => {


    const requestedHours = calculateRequestedHours();
    if (requestedHours <= 0) return false;
    if (!selectedUserBalance) return true;

    if (formData.event_type === "연차" || formData.event_type === "반차") {
      const available = selectedUserBalance.annual_leave_hours || 0;
      if (available < requestedHours) {
        alert(`연차 남은시간이 부족합니다. 남은시간을 확인하세요.\n(남은시간: ${available}시간, 신청시간: ${requestedHours}시간)`);
        return false;
      }
    } else if (formData.event_type === "대체휴무") {
      const available = selectedUserBalance.alt_leave_hours || 0;
      if (available < requestedHours) {
        alert(`대체휴무 남은시간이 부족합니다. 남은시간을 확인하세요.\n(남은시간: ${available}시간, 신청시간: ${requestedHours}시간)`);
        return false;
      }
    } else if (formData.event_type === "특별휴가") {
      const available = selectedUserBalance.special_leave_hours || 0;
      if (available < requestedHours) {
        alert(`특별휴가 남은시간이 부족합니다. 남은시간을 확인하세요.\n(남은시간: ${available}시간, 신청시간: ${requestedHours}시간)`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!formData.assignee) {
      alert("담당자를 선택하세요!");
      return;
    }
    if (formData.event_type === "행사" && !formData.title.trim()) {
      alert("제목을 입력하세요!");
      return;
    }
    if (PERFORMANCE_INPUT_TYPES.includes(formData.event_type) && !formData.work_category) {
      alert("업무 항목을 선택하세요!");
      return;
    }

    // 공통 payload (날짜 제외)
    const base = {
      event_type: formData.event_type,
      start_time: normalizeTime(formData.startTime, "09:00:00"),
      end_time: normalizeTime(formData.endTime, "18:00:00"),
      break_hours: formData.break_hours,
      is_notice: false,
      description: formData.description,
      assignee: formData.assignee,
    };
    // 제목 직접 입력 유형(행사·근무시간 변동)만 title을 보낸다.
    // 근무시간 변동은 비워 보내도 서버가 유형명으로 채운다(모델 save).
    if (TITLE_INPUT_TYPES.includes(formData.event_type)) {
      base.title = formData.title;
    }
    if (PERFORMANCE_INPUT_TYPES.includes(formData.event_type)) {
      base.work_category = formData.work_category;
      base.location = formData.location;
      base.difficulty = formData.difficulty ? parseInt(formData.difficulty) : null;
      base.travel_hours = formData.travel_hours;
    }

    const isWorkType = WORKDAY_SPLIT_TYPES.includes(formData.event_type);

    // ✅ 업무일정/행사: 주말·공휴일 제외하고 하루 단위로 분할 등록
    if (isWorkType) {
      if (calcDayHours() <= 0) {
        alert("시간 설정이 잘못되었습니다.\n종료 시간이 시작 시간보다 빠르거나 휴게시간이 너무 깁니다.");
        return;
      }

      const dates = buildWorkingDates();
      if (dates.length === 0) {
        alert("등록할 근무일이 없습니다.\n근무 요일 / 공휴일 근무 설정을 확인하세요.");
        return;
      }

      const failed = [];
      for (const d of dates) {
        const payload = { ...base, start_date: d, end_date: d };
        console.log("📤 POST payload >>>", payload);
        try {
          await onSubmit(payload);
        } catch (err) {
          console.error(`❌ ${d} 등록 실패`, err);
          failed.push(d);
        }
      }

      const total = dates.length;
      const failCount = failed.length;
      const okCount = total - failCount;

      if (failCount === 0) {
        // 전부 성공
        alert(`✅ 총 ${total}일 등록 완료되었습니다.`);
      } else if (okCount === 0) {
        // 전부 실패
        alert(`❌ 등록 실패\n총 ${total}일 모두 등록되지 않았습니다.\n실패한 날짜:\n${failed.join("\n")}`);
      } else {
        // 일부 실패
        alert(
          `⚠️ 일부만 등록되었습니다.\n` +
          `성공: ${okCount}일 / 실패: ${failCount}일\n\n` +
          `등록 실패한 날짜:\n${failed.join("\n")}`
        );
      }

      onClose();
      return;
    }




    // ✅ 휴가 4종: 기존 단일 일정 방식 그대로
    if (calcDayHours() <= 0) {
      alert("시간 설정이 잘못되었습니다.\n종료 시간이 시작 시간보다 빠르거나 휴게시간이 너무 깁니다.");
      return;
    }
    // ✅ 기간이 전부 주말·공휴일이면 차감할 날이 없다 — 업무일정과 같은 정책으로 차단
    if (buildLeaveDates().length === 0) {
      alert("차감할 근무일이 없습니다.\n선택한 기간이 전부 주말·공휴일입니다. 날짜를 확인하세요.");
      return;
    }
    const requestedHours = calculateRequestedHours();
    if (requestedHours <= 0) {
      alert("시간 설정이 잘못되었습니다.\n종료 시간이 시작 시간보다 빠르거나 휴게시간이 너무 깁니다.");
      return;
    }
    if (!checkLeaveBalance()) {
      return;
    }

    const payload = {
      ...base,
      start_date: formData.startDate,
      end_date: formData.endDate,
    };
    console.log("📤 POST payload >>>", payload);
    try {
      await onSubmit(payload);
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        data?.non_field_errors?.[0] ||
        (typeof data === "string" ? data : null) ||
        "일정 등록에 실패했습니다.";
      alert(msg);
      return; // 모달 닫지 않음
    }
    onClose();
  };





  const timeOptions = generateTimeOptions();
  if (!isOpen) return null;

  const isLeave = ["연차", "반차", "대체휴무", "특별휴가"].includes(formData.event_type);
  const isWork = WORKDAY_SPLIT_TYPES.includes(formData.event_type);


  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* ✅ Step 1: 담당자 선택 */}
        {step === 1 && (
          <>
            <h3 style={{ marginBottom: "16px" }}>담당자를 선택하세요</h3>
            <div className={styles.inputRow}>
              <span className={styles.inputLabelInline}>담당자</span>
              <select
                name="assignee"
                value={formData.assignee}
                onChange={handleChange}
                className={styles.inlineInput}
              >
                <option value="">담당자 선택</option>
                {users
                  .filter((u) => u.name && u.name !== "테스트" && isActiveStaff(u))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.position}
                    </option>
                  ))}
              </select>
            </div>
            <div className={styles.modalActions}>
              <button onClick={onClose} className={styles.cancelButton}>취소</button>
              <button
                onClick={() => {
                  if (!formData.assignee) {
                    alert("담당자를 선택하세요!");
                    return;
                  }
                  setStep(2);
                }}
                className={styles.saveButton}
              >
                다음
              </button>
            </div>
          </>
        )}

        {/* ✅ Step 2: 일정 유형 선택 */}
        {step === 2 && (
          <>
            <h3 style={{ marginBottom: "20px" }}>일정 유형을 선택하세요</h3>
            <div className={styles.typeSection}>
              {selectableEventTypesFor(currentUser()).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={formData.event_type === t ? styles.typeButtonActive : styles.typeButton}
                  onClick={() => setFormData((prev) => ({ ...prev, event_type: t }))}
                >
                  {t}
                </button>
              ))}
            </div>
            {!isCalendarAdmin(currentUser()) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>연차·반차는</span>
                <button
                  type="button"
                  onClick={goLeaveRequest}
                  title={REQUEST_ONLY_NOTICE}
                  style={{
                    width: "auto", height: 32, padding: "0 12px", border: "1px solid #1a6b1a",
                    background: "#fff", color: "#1a6b1a", borderRadius: 4, fontSize: 13,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  휴가신청서로 신청하기
                </button>
              </div>
            )}
            <div className={styles.modalActions}>
              <button onClick={() => setStep(1)} className={styles.cancelButton}>이전</button>
              <button onClick={() => setStep(3)} className={styles.saveButton}>다음</button>
            </div>
          </>
        )}

        {/* ✅ Step 3: 시간 및 상세 정보 */}
        {step === 3 && (
          <>
            {TITLE_INPUT_TYPES.includes(formData.event_type) && (
              <div className={styles.inputRow}>
                <span className={styles.inputLabelInline}>제목</span>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder={
                    // 행사만 제목이 필수(비워도 서버가 안 채운다). 나머지 제목 직접
                    // 입력 유형은 비워 두면 서버가 유형명으로 채우므로, 유형명을
                    // 문구에 그대로 끼워 넣어 유형이 늘어도 이 줄을 안 고치게 한다.
                    formData.event_type === "행사"
                      ? "행사 제목을 입력하세요"
                      : `내용을 입력하세요 (비워두면 '${formData.event_type}')`
                  }
                  className={styles.inlineInput}
                />
              </div>
            )}

            {formData.event_type === "업무일정" && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>업무 항목</label>
                  <div className={styles.categoryRow}>
                    <select
                      name="work_category"
                      value={formData.work_category}
                      onChange={handleChange}
                      className={styles.categorySelect}
                    >
                      <option value="">항목 선택</option>
                      {workCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowCategoryManage(true)}
                      className={styles.categoryManageButton}
                    >
                      ＋ 항목 관리
                    </button>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>장소/대상</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="예: ○○어린이집"
                    list="location-suggestions"
                    className={styles.inlineInput}
                  />
                  <datalist id="location-suggestions">
                    {locationSuggestions.map((loc, i) => (
                      <option key={i} value={loc} />
                    ))}
                  </datalist>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>업무 난이도 (1~10 / 기본 = 난이도 5)</label>
                  <select name="difficulty" value={formData.difficulty} onChange={handleChange}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.inputLabel}>이동시간</label>
                  <select name="travel_hours" value={formData.travel_hours} onChange={handleChange}>
                    <option value={0}>없음</option>
                    <option value={0.5}>30분</option>
                    <option value={1}>1시간</option>
                    <option value={1.5}>1시간 30분</option>
                    <option value={2}>2시간</option>
                  </select>
                </div>
              </>
            )}

            {selectedUserBalance && isLeave && (
              <div style={{
                padding: "8px",
                backgroundColor: "#e0f7fa",
                borderRadius: "4px",
                marginTop: "10px",
                marginBottom: "10px",
                fontSize: "13px"
              }}>
                {(formData.event_type === "연차" || formData.event_type === "반차") && (
                  <>
                    <div>연차 남은시간: <strong>{selectedUserBalance.annual_leave_hours || 0}시간</strong></div>
                    <div>신청 시간: <strong>{calculateRequestedHours() === -1 ? 0 : calculateRequestedHours()}시간</strong></div>
                  </>
                )}
                {formData.event_type === "대체휴무" && (
                  <>
                    <div>대체휴무 남은시간: <strong>{selectedUserBalance.alt_leave_hours || 0}시간</strong></div>
                    <div>신청 시간: <strong>{calculateRequestedHours() === -1 ? 0 : calculateRequestedHours()}시간</strong></div>
                  </>
                )}
                {formData.event_type === "특별휴가" && (
                  <>
                    <div>특별휴가 남은시간: <strong>{selectedUserBalance.special_leave_hours || 0}시간</strong></div>
                    <div>신청 시간: <strong>{calculateRequestedHours() === -1 ? 0 : calculateRequestedHours()}시간</strong></div>
                  </>
                )}
              </div>
            )}

            <div className={styles.dateRow}>
              <div>
                <label className={styles.inputLabel}>시작 날짜</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={styles.dateInput}
                />
              </div>

              <div>
                <label className={styles.inputLabel}>종료 날짜</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className={styles.dateInput}
                />
              </div>
            </div>

            {/* ✅ 근무 요일 / 공휴일 근무 (업무일정·행사 전용) */}
            {isWork && (
              <div className={styles.formGroup}>
                <label className={styles.inputLabel}>근무 요일</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "4px" }}>
                  {WEEKDAYS.map(({ dow, label }) => (
                    <label key={dow} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "14px" }}>
                      <input
                        type="checkbox"
                        checked={!!workDays[dow]}
                        onChange={(e) => setWorkDays((prev) => ({ ...prev, [dow]: e.target.checked }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                  체크된 요일만 등록됩니다. (기본: 평일만)
                </div>
              </div>
            )}
            
            {isWork && holidaysInRange.length > 0 && (
              <div className={styles.formGroup}>
                <label className={styles.inputLabel}>공휴일 근무</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                  {holidaysInRange.map((h) => (
                    <label
                      key={h.id ?? h.date}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        padding: "6px 8px",
                        border: "1px solid #eee",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!workedHolidays[h.date]}
                        onChange={(e) => setWorkedHolidays((prev) => ({ ...prev, [h.date]: e.target.checked }))}
                        style={{ width: "16px", height: "16px", flexShrink: 0, margin: 0 }}
                      />
                      <span style={{ color: "#d32f2f", fontWeight: 600, flexShrink: 0 }}>
                        {h.date}
                      </span>
                      <span style={{ color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {h.name} ({h.holiday_type})
                      </span>
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: "12px", color: "#888", marginTop: "6px" }}>
                  체크한 공휴일만 근무로 등록됩니다. (기본: 미등록)
                </div>
              </div>
            )}




            <div className={styles.formGroup}>
              <label className={styles.inputLabel}>시작 시간</label>
              <select name="startTime" value={formData.startTime} onChange={handleChange}>


                {timeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.inputLabel}>휴게시간</label>
              <select name="break_hours" value={formData.break_hours} onChange={handleChange}>
                <option value={0}>없음</option>
                <option value={0.5}>30분</option>
                <option value={1}>1시간</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.inputLabel}>종료 시간</label>
              <select name="endTime" value={formData.endTime} onChange={handleChange}>
                {timeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.inputLabel}>설명</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="설명을 입력하세요..."
                className={styles.descriptionInput}
              />
            </div>

            <div className={styles.modalActions}>
              <button onClick={() => setStep(2)} className={styles.cancelButton}>이전</button>
              <button onClick={handleSubmit} className={styles.saveButton}>저장</button>
            </div>
          </>
        )}
      </div>

      <WorkCategoryManageModal
        isOpen={showCategoryManage}
        onClose={() => setShowCategoryManage(false)}
        onChanged={() => {
          api
            .get("/calendar/work-categories/")
            .then((res) => setWorkCategories(res.data || []))
            .catch(() => {});
        }}
      />
    </div>
  );
}